import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Eye, Clock, Lock, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AssistantHobbiesView({ user, program, onBack }) {
  const [config, setConfig] = useState(null);
  const [selections, setSelections] = useState([]);
  const [campers, setCampers] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = program ? { program } : {};
        const [configRes, selRes, campersRes] = await Promise.all([
          axios.get(`${API}/hobbies/config`, { headers, params }),
          axios.get(`${API}/hobbies/selections`, { headers, params }),
          axios.get(`${API}/campers`, { headers }),
        ]);
        setConfig(configRes.data);
        setSelections(selRes.data || []);
        setCampers((campersRes.data || []).sort((a, b) =>
          (a.last_name || "").localeCompare(b.last_name || "")));
      } catch {
        toast.error("Failed to load selections");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [program]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fuchsia-600"></div>
      </div>
    );
  }

  const weeks = config?.weeks || [];
  const currentWeekNum = config?.current_week?.week;
  const isOpen = config?.current_week != null;
  const prog = config?.program || "mariner";
  const title = prog === "intensive"
    ? "Navigator Intensives"
    : prog === "navigator"
    ? "Navigator Hobbies"
    : `${config?.division === "Mariner B" ? "Boys" : "Girls"} Mariner Hobbies`;

  const selMap = {};
  (selections || []).forEach((s) => {
    if (!selMap[s.camper_id]) selMap[s.camper_id] = {};
    selMap[s.camper_id][s.week_number] = s.activity_selected;
  });

  return (
    <div className="min-h-screen bg-background" data-testid="assistant-hobbies-view">
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack} className="px-2 gap-1" data-testid="assistant-hobbies-back">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs font-medium">Dashboard</span>
            </Button>
            <div>
              <h1 className="text-sm font-bold text-fuchsia-700 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" /> {title}
              </h1>
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Eye className="w-3 h-3" /> View only — selections are made by your head counselor
              </p>
            </div>
          </div>
          {isOpen ? (
            <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
              <Clock className="w-3 h-3 mr-1" /> Week {currentWeekNum}
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">
              <Lock className="w-3 h-3 mr-1" /> Window Closed
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-3 sm:p-4">
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs" data-testid="assistant-hobbies-table">
              <thead>
                <tr className="border-b bg-fuchsia-50/60">
                  <th className="text-left px-3 py-2 font-bold text-fuchsia-900 sticky left-0 bg-fuchsia-50/60">Camper</th>
                  {weeks.map((w) => (
                    <th
                      key={w.week}
                      className={`px-2 py-2 font-bold whitespace-nowrap ${w.week === currentWeekNum ? "text-green-700" : "text-gray-500"}`}
                    >
                      Wk {w.week}{w.week === currentWeekNum ? " •" : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {campers.map((c) => {
                  const name = `${c.first_name} ${c.last_name}`;
                  return (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`assistant-hobbies-row-${c.id}`}>
                      <td className="px-3 py-2 font-semibold text-gray-900 whitespace-nowrap sticky left-0 bg-white">{name}</td>
                      {weeks.map((w) => {
                        const pick = (selMap[c.id] || {})[w.week];
                        const isCur = w.week === currentWeekNum;
                        return (
                          <td key={w.week} className={`px-2 py-2 text-center whitespace-nowrap ${isCur ? "bg-green-50/70" : ""}`}>
                            {pick ? (
                              <span className={`font-medium ${isCur ? "text-green-800" : "text-gray-700"}`}>{pick}</span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {campers.length === 0 && (
                  <tr><td className="px-3 py-6 text-center text-gray-400" colSpan={weeks.length + 1}>No campers found.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
        <p className="text-[10px] text-gray-400 mt-2 px-1">
          Blank cells mean your head counselor hasn't submitted that week's pick yet.
        </p>
      </main>
    </div>
  );
}
